/**
 * Lab fulfillment journey for one request. A forward-only stepper the provider advances through
 * CONFIRMED → SAMPLE_COLLECTED → PROCESSING → REPORT_READY. Each stage stamps a time; at Report
 * ready the provider attaches the report document(s) + optional notes and the owner is notified.
 * Reads the live fulfillment so it stays fresh after each advance. Modeled on the cremation modal.
 */
import { useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ImageGalleryEditor } from '@/components/image-gallery-editor';
import { form_error_message } from '@/lib/forms';
import { use_vendor_fulfillment, use_advance_fulfillment } from '@/features/lab-fulfillment/hooks';
import {
  advance_label,
  stage_label,
  LAB_STAGE_ORDER,
  LAB_STAGE_TIMESTAMP,
  type LabFulfillment,
} from '@/features/lab-fulfillment/types';
import type { Booking } from '@/features/bookings/types';

const field =
  'flex w-full rounded-input border-2 border-input bg-card px-3 py-2 text-sm shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:outline-none focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/15';

function format_stamp(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Shell({ title, on_close, children }: { title: string; on_close: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" onClick={on_close}>
      <div className="my-12 w-full max-w-lg rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="font-heading text-lg font-bold">{title}</h2>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-4 p-5">{children}</div>
      </div>
    </div>
  );
}

function Stepper({ f }: { f: LabFulfillment }) {
  const current_idx = LAB_STAGE_ORDER.indexOf(f.status);
  return (
    <ol className="flex flex-col" data-testid="lab-stepper">
      {LAB_STAGE_ORDER.map((stage, idx) => {
        const done = idx <= current_idx;
        const is_current = idx === current_idx;
        const stamp = format_stamp(f[LAB_STAGE_TIMESTAMP[stage]] as string | null);
        const last = idx === LAB_STAGE_ORDER.length - 1;
        return (
          <li key={stage} className="flex gap-3" data-testid={`lab-step-${stage}`}>
            <div className="flex flex-col items-center">
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold ${
                  done ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-card text-muted-foreground'
                }`}
              >
                {done ? <Check className="h-4 w-4" /> : idx + 1}
              </span>
              {!last && <span className={`w-0.5 flex-1 ${idx < current_idx ? 'bg-primary' : 'bg-border'}`} />}
            </div>
            <div className={`pb-5 ${last ? 'pb-0' : ''}`}>
              <p className={`text-sm font-semibold ${is_current ? 'text-primary' : done ? 'text-foreground' : 'text-muted-foreground'}`}>
                {stage_label(stage)}
              </p>
              {stamp && <p className="text-xs text-muted-foreground">{stamp}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function LabJourneyModal({ booking, on_close }: { booking: Booking; on_close: () => void }) {
  const f_q = use_vendor_fulfillment(booking.id);
  const advance_m = use_advance_fulfillment(booking.id);
  const [note, set_note] = useState('');
  const [report_notes, set_report_notes] = useState('');
  const [error, set_error] = useState<string | null>(null);

  const f = f_q.data;
  const current_idx = f ? LAB_STAGE_ORDER.indexOf(f.status) : -1;
  const next_stage = f && current_idx < LAB_STAGE_ORDER.length - 1 ? LAB_STAGE_ORDER[current_idx + 1] : null;
  const advancing_to_report = next_stage === 'REPORT_READY';
  // The report doc is useful to attach from PROCESSING onward (before/at marking ready).
  const can_attach_report = current_idx >= LAB_STAGE_ORDER.indexOf('PROCESSING');

  async function advance() {
    if (!next_stage) return;
    set_error(null);
    try {
      await advance_m.mutateAsync({
        to: next_stage,
        note: note.trim() || undefined,
        report_notes: advancing_to_report && report_notes.trim() ? report_notes.trim() : undefined,
      });
      set_note('');
      set_report_notes('');
    } catch (err) {
      set_error(form_error_message(err, 'Could not update the journey.'));
    }
  }

  return (
    <Shell title="Lab journey" on_close={on_close}>
      {f?.test_name && (
        <p className="text-sm text-muted-foreground" data-testid="lab-journey-test">
          <span className="font-semibold text-foreground">{f.test_name}</span>
          {f.sample_type ? ` · ${f.sample_type.toLowerCase()} sample` : ''}
          {f.turnaround_hours != null ? ` · ~${f.turnaround_hours}h turnaround` : ''}
        </p>
      )}

      {f_q.isLoading ? (
        <div className="flex items-center justify-center p-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : f_q.error || !f ? (
        <p className="text-sm text-destructive" data-testid="lab-journey-error">
          {form_error_message(f_q.error, 'This journey opens once you accept the request.')}
        </p>
      ) : (
        <>
          <Stepper f={f} />

          {/* Report document(s) — the finished report the owner downloads (≤5). */}
          {can_attach_report && (
            <div className="rounded-card border-2 border-dashed border-brand-outline-variant/30 p-3">
              <ImageGalleryEditor owner_type="LAB_REPORT" owner_id={booking.id} label="Report document(s)" />
            </div>
          )}

          {f.status === 'REPORT_READY' && f.report_notes && (
            <div className="rounded-card border-2 border-brand-outline-variant/20 bg-muted/40 p-3" data-testid="lab-report-notes">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Report notes</p>
              <p className="mt-1 whitespace-pre-wrap text-sm">{f.report_notes}</p>
            </div>
          )}

          {next_stage && (
            <div className="flex flex-col gap-2 rounded-card border-2 border-dashed border-brand-outline-variant/30 p-3">
              <textarea
                className={`${field} h-16 resize-y`}
                maxLength={2000}
                value={note}
                onChange={(e) => set_note(e.target.value)}
                placeholder="Add a note for this step (optional)"
                data-testid="lab-advance-note"
              />
              {advancing_to_report && (
                <textarea
                  className={`${field} h-16 resize-y`}
                  maxLength={2000}
                  value={report_notes}
                  onChange={(e) => set_report_notes(e.target.value)}
                  placeholder="Report notes for the owner (optional)"
                  data-testid="lab-report-notes-input"
                />
              )}
              {error && <p className="text-sm text-destructive" data-testid="lab-advance-error">{error}</p>}
              <div>
                <Button
                  variant="gradient"
                  size="sm"
                  onClick={advance}
                  disabled={advance_m.isPending}
                  data-testid="lab-advance"
                >
                  {advance_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {advance_label(next_stage)}
                </Button>
              </div>
            </div>
          )}

          {!next_stage && (
            <p className="text-sm text-muted-foreground">The report is ready — the owner has been notified.</p>
          )}
        </>
      )}
    </Shell>
  );
}
